import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { RegExp } from 'src/app/core/config/regex';
import { Context } from 'src/app/shared/models/context';
import { Image, Insurance, InsurancePlan, Message } from 'src/app/shared/models/domain';
import { CommonService } from 'src/app/shared/services/common.service';
import { DomainService } from 'src/app/shared/services/domain.service';
import { SettingsService } from 'src/app/shared/services/settings.service';

@Component({
  selector: 'app-insurance-crud',
  templateUrl: './insurance-crud.component.html',
  styleUrls: ['./insurance-crud.component.scss']
})
export class InsuranceCrudComponent implements OnInit {
  @Output() passEntry: EventEmitter<any> = new EventEmitter();
  @Input() insurance: Insurance;
  @Input() context: Context;
  @Input() insurances: Insurance[] = [];
  @Input() insuranceStatus: boolean;
  public insuranceForm: FormGroup;
  public submitted: boolean = false;
  private modified: boolean = false;
  public insurancePlanForm: FormGroup;
  public submittedInsurance: boolean = false;
  public insurancePlan: InsurancePlan;
  public insuranceId: number;
  public types: string;
  public years: number[] = [];
  public placeholderText: string = 'Select an established Year';
  private imageUrl: string;
  public image: string;
  constructor(
    private settingsService: SettingsService,
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private commonService: CommonService,
    private domainService: DomainService,
  ) { }

  ngOnInit(): void {
    this.years = this.commonService.generateYears(20);
    this.setInsurance(this.insurance);
    this.setInsurancePlanForm([]);
    if (this.insurance?.id) {
      this.getInsurancePlans();
    }
    this.settingsService.getInsuranceType().subscribe((type) => (this.types = type));
  }

 public async getAndSetBase64(event: any) {
     const image: string = await this.commonService.convertImgToBase64(event);
     if (!image) return;
     this.imageUrl = image;
   }
 
 
   private saveImage(sid: number, refType: string, label: string, url: string) {
     let image: Image = {
       id: null,
       sid: sid,
       type: refType,
       label: label,
       image: url,
     };
     image.image = url
     this.domainService.createImage(image).subscribe({
       next: (response: string) => {
       },
       error: (error: HttpErrorResponse) => {
       }
     });
   }
 
   private getImage() {
     this.domainService.getImage('INSURER', 'INSURER', this.insurance.id, 'avatar').subscribe(image => {
      //  this.image = image;
      this.image=image.payload;
     });
   }

  public setInsurance(insurance?: Insurance) {
    if (!insurance)
      insurance = {
        id: null,
        name: null,
        persona: "ADMIN",
        established: null,
        registrationNumber: null,
        pan: null,
        gstIn: null,
        label: null,
        email: null,
        contact: null,
        website: null,
        logo: null,
        licenceNumber: null,
        disabled: false
      } as Insurance;
    this.insuranceForm = this.fb.group({
      id: this.fb.control(insurance?.id || null),
      name: this.fb.control(insurance.name, [Validators.required, Validators.pattern(RegExp.name)]),
      persona: this.fb.control(insurance.persona, []),
      established: this.fb.control(insurance.established, [Validators.required]),
      registrationNumber: this.fb.control(insurance.registrationNumber, [Validators.required, Validators.pattern(RegExp.alphaNumeric)]),
      pan: this.fb.control(insurance.pan, [Validators.pattern(RegExp.pan)]),
      gstIn: this.fb.control(insurance.gstIn, [Validators.pattern(RegExp.gst)]),
      label: this.fb.control(insurance.label, [Validators.pattern(RegExp.label)]),
      email: this.fb.control(insurance.email, [Validators.pattern(RegExp.email)]),
      contact: this.fb.control(insurance.contact, [Validators.pattern(RegExp.contact)]),
      website: this.fb.control(insurance.website, [Validators.pattern(RegExp.website)]),
      logo: this.fb.control(insurance.logo, []),
      licenceNumber: this.fb.control(insurance.licenceNumber, [Validators.required]),
      disabled: this.fb.control(insurance.disabled, []),
    });
    this.insuranceForm.valueChanges.subscribe(value => this.modified = true);
  }

  get i() {
    return this.insuranceForm as FormGroup;
  }

  private getInsurance(id: number, message: Message) {
    this.settingsService.getInsurance(id).subscribe(insurance => {
      this.passEntry.emit(message);
      if (insurance.id) {
        this.setInsurance(insurance);

      }
    });
  }

  public saveInsurance(inProgress?: any) {
    this.submitted = true;
    if (this.insuranceForm.valid) {
      const insurance: Insurance = this.insuranceForm.value;
      if (!this.modified) {
        this.activeModal.close(false);
        this.commonService.setToastr(200, `Insurance ${insurance.name} has been updated successfully!`);
        return;
      }
      inProgress.value = true;
      if (insurance.id) {
        this.settingsService.updateInsurance(insurance.id, insurance).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              this.insuranceForm.get('id').setValue(parseInt(response?.payload));
              this.getInsurance(insurance.id, response);
              if(this.imageUrl){
                this.saveImage(insurance.id, 'INSURER', 'avatar', this.imageUrl);
              }
              inProgress.value = false;
              this.commonService.setToastr(200, `Insurance ${insurance.name} has been updated successfully!`);
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      } else {
        this.settingsService.createInsurance(insurance).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              this.commonService.setToastr(200, `Insurance ${insurance.name} has been created successfully!`);
              this.insuranceForm.get('id').setValue(parseInt(response?.payload));
              this.getInsurance(parseInt(response.payload), response);
              if(this.imageUrl){
                this.saveImage(parseInt(response.payload), 'INSURER', 'Logo', this.imageUrl);
              }
              inProgress.value = false;
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, response.text);
            }
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
            this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      }
    }
  }

  public getInsurancePlans() {
    this.settingsService.getInsurancePlans(this.insuranceForm?.value?.id).subscribe((insurancePlans: InsurancePlan[]) => {
      if (this.insuranceForm?.value?.id) {
        this.setInsurancePlanForm(insurancePlans);
      }
    });
  }

  public get insurancePlans(): FormArray {
    return this.insurancePlanForm.get('insurancePlans') as FormArray;
  }

  public addInsurancePlan(insurancePlan?: InsurancePlan) {
    this.insurancePlans.push(this.createPlan(insurancePlan));
  }

  private setInsurancePlanForm(insurancePlans: InsurancePlan[]) {
    this.insurancePlanForm = this.fb.group({
      insurancePlans: this.fb.array([]),
    });

    if (!insurancePlans || insurancePlans.length === 0) {
      this.addInsurancePlan();
    }
    insurancePlans.forEach(plan => this.addInsurancePlan(plan));
  }

  private createPlan(insurancePlan?: InsurancePlan): FormGroup {
    return this.fb.group({
      id: this.fb.control(insurancePlan?.id || null, []),
      name: this.fb.control(insurancePlan?.name, [Validators.required, Validators.pattern(RegExp.name)]),
      type: this.fb.control(insurancePlan?.type, [Validators.required]),
      premium: this.fb.control(insurancePlan?.premium, [Validators.required, Validators.pattern(RegExp.number)]),
      coverage: this.fb.control(insurancePlan?.coverage? Number(insurancePlan.coverage).toLocaleString('en-IN')
          : null, [Validators.required, Validators.pattern(RegExp.coverage)] ),
      tenure: this.fb.control(insurancePlan?.tenure, [Validators.required,  Validators.min(1),
        Validators.max(12), Validators.pattern(RegExp.number)]),
      features: this.fb.control(insurancePlan?.features, [Validators.required]),
      agreement: this.fb.control(insurancePlan?.agreement, [Validators.required]),
      active: this.fb.control(insurancePlan?.active || false, []),
      disabled: this.fb.control(insurancePlan?.disabled || false, []),
      insuranceId: this.fb.control(insurancePlan?.insuranceId || this.insuranceForm?.value?.id, []),
      insuranceName: this.fb.control(insurancePlan?.insuranceName, []),
    });
  }

  formatCoverage(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/,/g, ''); // Remove existing commas
    if (!isNaN(Number(value))) {
      input.value = Number(value).toLocaleString('en-IN'); // Format for display
      this.insurancePlans.controls.forEach((control) => {
        if (control.get('coverage')?.value === value) {
          control.patchValue({ coverage: value }); // Store raw value in the form
        }
      });
    }
  }
  public deleteInsurancePlan(index: number, insurancePlan: FormGroup): void {
    if (insurancePlan.valid) {
      const planId = insurancePlan.value?.id;
      if (planId) {
        this.settingsService.deleteInsurancePlan(planId).subscribe({
          next: (message: any) => {
            if (message.text) {
              if (this.insurancePlans.length > 1) {
                this.insurancePlans.removeAt(index);
              } else {
                this.insurancePlans.at(index).reset();
              }
              this.commonService.setToastr(200, message.text);
            } else {
              this.commonService.setToastr(500, 'Something went wrong! Please try again.');
            }
          },
          error: (error: HttpErrorResponse) => {
            this.commonService.setToastr(error.status, error?.error?.response || error.message);
          },
        });
      } else {
        this.insurancePlans.removeAt(index);
        this.commonService.setToastr(200, 'Insurance plan deleted successfully');
        if (this.insurancePlans.length < 1) {
          this.addInsurancePlan();
        }
      }
    }
  }
  public saveInsurancePlan(inProgress: any) {
    this.submitted = true;
    this.insurancePlanForm.markAllAsTouched();
    if (this.insurancePlanForm.invalid) {
      this.toastr.error('Please fill all required fields correctly.');
      return;
    }
    const insuranceId = this.insuranceForm.value.id;
    const plans = this.insurancePlans.value.map((plan: InsurancePlan) => ({
      ...plan,
      insuranceId: insuranceId,
      coverage: plan.coverage?.toString().replace(/,/g, ''),
    }));
    const duplicatePlanIndex = plans.findIndex(
      (plan, index, self) =>
        self.findIndex(p => p.name === plan.name && p.coverage === plan.coverage && p.premium === plan.premium) !== index
    );
   
    if (duplicatePlanIndex !== -1) {
      const duplicatePlan = plans[duplicatePlanIndex];
      this.commonService.setToastr(
        400,
        `The Insurance Plan name with coverage and premium already exists!`
      );
      return;
    }
   
    const duplicateNameIndex = plans.findIndex(
      (plan, index, self) => self.findIndex(p => p.name === plan.name) !== index
    );
    const duplicateCoverageIndex = plans.findIndex(
      (plan, index, self) => self.findIndex(p => p.coverage === plan.coverage) !== index
    );
    const duplicatePremiumIndex = plans.findIndex(
      (plan, index, self) => self.findIndex(p => p.premium === plan.premium) !== index
    );
   
    if (duplicateNameIndex !== -1 && duplicateCoverageIndex !== -1) {
      const duplicatePlan = plans[duplicateNameIndex]; // Any index will work here
      this.commonService.setToastr(
        400,
        `Insurance Plan name and coverage already exists!`
      );
      return;
    }
   
    if (duplicateNameIndex !== -1 && duplicatePremiumIndex !== -1) {
      const duplicatePlan = plans[duplicateNameIndex];
      this.commonService.setToastr(
        400,
        `Insurance Plan name and premium already exists!`
      );
      return;
    }
   
    if (duplicateCoverageIndex !== -1 && duplicatePremiumIndex !== -1) {
      const duplicatePlan = plans[duplicateCoverageIndex];
      this.commonService.setToastr(
        400,
        `Insurance Plan  coverage and premium already exists!`
      );
      return;
    }
   
    if (duplicateNameIndex !== -1) {
      const duplicatePlan = plans[duplicateNameIndex];
      this.commonService.setToastr(
        400,
        `Insurance Plan name already exists!`
      );
      return;
    }
   
    if (duplicateCoverageIndex !== -1) {
      const duplicatePlan = plans[duplicateCoverageIndex];
      this.commonService.setToastr(
        400,
        `Insurance Plan coverage already exists!`
      );
      return;
    }
   
    if (duplicatePremiumIndex !== -1) {
      const duplicatePlan = plans[duplicatePremiumIndex];
      this.commonService.setToastr(
        400,
        `Insurance Plan  premium already exists!`
      );
      return;
    }
   
    const plansToUpdate = plans.filter((plan: InsurancePlan) => plan.id);
    const plansToCreate = plans.filter((plan: InsurancePlan) => !plan.id);


    inProgress.value = true;


    if (plansToUpdate.length > 0) {
      this.updateInsurancePlans(plansToUpdate, inProgress);
    }


    const isUpdateProcess = plansToUpdate.length > 0;
    if (plansToCreate.length > 0) {
      this.createInsurancePlans(plansToCreate, inProgress, isUpdateProcess);
    }


    if (plansToUpdate.length === 0 && plansToCreate.length === 0) {
      inProgress.value = false;
    }
  }


  private updateInsurancePlans(plansToUpdate: InsurancePlan[], inProgress: any, isUpdateProcess: boolean = false) {
    this.settingsService.updateInsurancePlan(plansToUpdate).subscribe({
      next: (message: Message) => {
        this.getInsurancePlans();
        this.commonService.setToastr(200, message.text);
        inProgress.value = false;

      },
      error: (error: HttpErrorResponse) => {
        this.toastr.error(error.error?.response || error.message, 'Error');
        inProgress.value = false;
      },
    });

  }

  private createInsurancePlans(plansToCreate: InsurancePlan[], inProgress: any, isUpdateProcess: boolean = false) {
    if (plansToCreate.length > 0) {
      this.settingsService.createInsurancePlan(plansToCreate).subscribe({
        next: (message: Message) => {
          if (!isUpdateProcess && !message.failed) {
            this.getInsurancePlans();
            this.commonService.setToastr(200, message.text);
            inProgress.value = false;
          } else if (message.failed) {
            this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            inProgress.value = false;
          }
        },
      });
    }
  }

  get truncatedPlaceholder(): string {
    const maxLength = 20;
    return this.placeholderText.length > maxLength
      ? this.placeholderText.slice(0, maxLength) + '...'
      : this.placeholderText;
  }
}


